import { call as webCall } from '../utils/appData';
/* eslint-disable default-case */
import { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { calculatePurchaseFee, isHostUser } from "../utils/roleUtils";
import { httpsCallable } from "firebase/functions";
import { functions, database } from "../constants/firebase";
import { ref, get } from "firebase/database";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import useExchangeRates from "../hooks/useExchangeRates";

const ProductCheckout = ({
    product,
    isOpen,
    onClose,
    displayPrice,
    displayCurrency,
    userCountry,
}) => {
    const { currentUser } = useUser();
    const navigate = useMoveTo();
    const [loading, setLoading] = useState(false);
    const [isInApp, setIsInApp] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const { convertPrice } = useExchangeRates();

    // Detect if running in React Native WebView
    useEffect(() => {
        if (window.isInApp !== undefined) {
            setIsInApp(window.isInApp);
            setIsIOS(window.isIOS || false);
            setIsAndroid(window.isAndroid || false);
        } else {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            setIsInApp(userAgent.includes('ZylenApp'));
            setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
            setIsAndroid(/android/i.test(userAgent));
        }
    }, []);

    // Listen for payment results from React Native
    useEffect(() => {
        const handleMessage = (event) => {
            try {
                const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                switch (message.type) {
                    case 'STRIPE_PAYMENT_SUCCESS':
                        window.showToast("Payment successful! 🎉", "success");
                        // Navigate to success page
                        navigate(`${ROUTES.PRODUCT_DETAILS}/${product?.id}?purchase=success`, { replace: true });
                        onClose();
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_CANCELED':
                        window.showToast("Payment cancelled", "info");
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_ERROR':
                        window.showToast(message.error || "Payment failed. Please try again.", "error");
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_VERIFICATION_FAILED':
                        window.showToast("Payment verification failed. Please contact support.", "error");
                        setLoading(false);
                        break;

                    case 'PAYSTACK_PAYMENT_COMPLETED':
                        window.showToast("Payment successful! 🎉", "success");
                        navigate(`${ROUTES.PRODUCT_DETAILS}/${product?.id}?purchase=success`, { replace: true });
                        onClose();
                        setLoading(false);
                        break;

                    case 'PAYSTACK_PAYMENT_CANCELED':
                        window.showToast("Payment cancelled", "info");
                        setLoading(false);
                        break;

                    case 'PAYSTACK_PAYMENT_ERROR':
                        window.showToast(message.error || "Payment failed. Please try again.", "error");
                        setLoading(false);
                        break;

                    case 'PAYSTACK_PAYMENT_VERIFICATION_FAILED':
                        window.showToast("Payment verification failed. Please contact support.", "error");
                        setLoading(false);
                        break;

                    case 'STRIPE_PLATFORM_NOT_SUPPORTED':
                        window.showToast(
                            `${message.platform === 'ios' ? 'Apple Pay' : 'Google Pay'} is not available on this device.`,
                            "error"
                        );
                        setLoading(false);
                        break;
                }
            } catch (e) {
                // Not a JSON message, ignore
            }
        };

        if (isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, [isInApp, product, navigate, onClose]);

    if (!isOpen || !product) return null;

    const originalBasePrice = parseFloat(displayPrice ?? product.price) || 0;

    // ✅ Apply 15% app markup for in-app users
    const appPlatformFeeRate = 0.15; // 15% Apple/Google fee
    const basePriceAfterAppMarkup = isInApp
        ? originalBasePrice * (1 + appPlatformFeeRate)
        : originalBasePrice;

    // ✅ Calculate service fee (5% for free users) on the base price (including app markup)
    const { fee: serviceFee, total: finalTotal } = calculatePurchaseFee(currentUser, basePriceAfterAppMarkup);
    const isFreeUser = !isHostUser(currentUser);

    // ✅ For Stripe, we need to pass the original base price (without app markup)
    // because the app markup is handled separately
    const basePriceForStripe = originalBasePrice;
    const serviceFeeForStripe = isFreeUser ? originalBasePrice * 0.05 : 0;
    const totalForStripe = originalBasePrice + serviceFeeForStripe;

    const getCurrencySymbol = () => {
        if (displayCurrency) {
            return displayCurrency === "ZAR" ? "R" : "$";
        }
        return product.currency === 'ZAR' ? 'R' : '$';
    };

    const paymentCurrency =
        (displayCurrency || product.currency || "USD").toLowerCase();

    const currencySymbol = getCurrencySymbol();

    const convertToZAR = (amount, currency) => {
        if (!amount) return 0;

        const curr = currency?.toUpperCase();

        if (curr === "ZAR") {
            return amount;
        }

        if (curr === "USD") {
            const convertedAmount = convertPrice(amount, "USD", "ZAR");
            return convertedAmount;
        }

        return amount;
    };

    const handlePaystackPayment = async (sellerData, product, buyerData, basePrice, serviceFee, totalPrice) => {
        try {
            const { quote } = await webCall('createPaymentQuote', { type:'product', amount:Math.round(totalPrice*100), currency:'ZAR', buyerId:buyerData.uid, sellerId:product.userId, productId:product.id });
            const params = new URLSearchParams({ productId:product.id, type:'product', quoteId:quote.quoteId });
            const result = await webCall('initializePaystackPayment', {
                email:buyerData.email, amount:quote.amount, currency:quote.currency, quoteId:quote.quoteId,
                subaccount:sellerData.paystackSubaccountCode, bearer:'subaccount',
                callback_url:`${window.location.origin}/paystack-redirect.html?${params}`,
                metadata:{ type:'product', productId:product.id, sellerId:product.userId, buyerId:buyerData.uid, firebaseUID:buyerData.uid, basePrice, serviceFee, totalPrice, quoteId:quote.quoteId, platform:'web' },
                transaction_charge:Math.round(serviceFee*100),
            });
            if(!result.status || !result.data?.authorization_url)throw new Error(result.message||'Could not start payment.');
            window.location.assign(result.data.authorization_url);
        } catch(error) { window.showToast(error.message||'Payment could not start.','error'); }
        finally { setLoading(false); }
    };

    const handlePurchase = async () => {
        if (!currentUser?.uid) {
            window.showToast("Please login to purchase products.", "error");
            return;
        }

        if (product.userId === currentUser.uid) {
            window.showToast("You cannot purchase your own course.", "error");
            return;
        }

        setLoading(true);
        try {
            const sellerRef = ref(database, `Users/${product.userId}`);
            const sellerSnap = await get(sellerRef);
            if (!sellerSnap.exists()) throw new Error("Seller not found.");
            const sellerData = sellerSnap.val();

            const isStripeSeller =
                sellerData?.stripeConnectedAccountId ||
                (sellerData?.payoutMethod === "stripe" && !!sellerData?.stripeConnectedAccountId);
            const isPaystackSeller =
                sellerData?.paystackSubaccountCode || sellerData?.payoutMethod === "paystack";

            if (!isStripeSeller && !isPaystackSeller) {
                throw new Error("This seller hasn't completed their payout setup yet. Please contact the course creator.");
            }


            if (isStripeSeller) {
                const createProductCheckout = httpsCallable(functions, "createProductCheckoutSession");

                // Common data for both web and in-app
                const checkoutData = {
                    userId: currentUser.uid,
                    email: currentUser.email,
                    productId: product.id,
                    productTitle: product.title,
                    basePrice: basePriceForStripe,
                    serviceFee: serviceFeeForStripe,
                    totalPrice: totalForStripe,
                    currency: paymentCurrency.toLowerCase(),
                };

                if (isInApp) {
                    // For in-app purchases - use PaymentIntent
                    const result = await createProductCheckout({
                        ...checkoutData,
                        inApp: true,
                        platform: isIOS ? 'ios' : (isAndroid ? 'android' : 'mobile'),
                        includeAppMarkup: true,
                        appMarkupRate: 0.15,
                    });

                    if (result.data.clientSecret) {
                        // Send to React Native to show Apple Pay/Google Pay
                        window.ReactNativeWebView.postMessage(JSON.stringify({
                            type: 'STRIPE_CHECKOUT',
                            sessionId: result.data.sessionId,
                            clientSecret: result.data.clientSecret,
                            ephemeralKey: result.data.ephemeralKey,
                            customerId: result.data.customerId,
                            merchantDisplayName: product.title,
                            currency: paymentCurrency,
                            productId: product.id,
                            userId: currentUser.uid,
                            sellerId: product.userId,
                            appMarkupApplied: true,
                            appMarkupRate: appPlatformFeeRate,
                            totalPrice: totalForStripe,
                        }));
                    } else {
                        throw new Error("Failed to create Stripe payment intent");
                    }
                } else {
                    // For web purchases - use Checkout Session
                    const result = await createProductCheckout({
                        ...checkoutData,
                        inApp: false,
                        successUrl: `${window.location.origin}/#${ROUTES.PRODUCT_DETAILS}/${product.id}?purchase=success`,
                        cancelUrl: `${window.location.origin}/#${ROUTES.PRODUCT_DETAILS}/${product.id}?purchase=cancelled`,
                    });

                    if (result.data.url) {
                        window.location.href = result.data.url;
                    } else {
                        throw new Error("Failed to create Stripe checkout session");
                    }
                }
            } else if (isPaystackSeller) {
                // Convert base price to ZAR first
                const basePriceZar = convertToZAR(originalBasePrice, paymentCurrency);

                // Apply app markup if needed
                const basePriceAfterMarkupZar = isInApp
                    ? basePriceZar * (1 + appPlatformFeeRate)
                    : basePriceZar;

                // Calculate service fee from ZAR price after markup
                const { fee: serviceFeeZar, total: finalTotalZar } =
                    calculatePurchaseFee(currentUser, basePriceAfterMarkupZar);

                await handlePaystackPayment(
                    sellerData,
                    product,
                    currentUser,
                    basePriceAfterMarkupZar,
                    serviceFeeZar,
                    finalTotalZar
                );
            }
        } catch (error) {
            console.error("Error creating checkout:", error);
            window.showToast(error.message || "Checkout failed. Please try again.", "error");
            setLoading(false);
        }
    };

    const handleUpgrade = () => {
        onClose();
        navigate(ROUTES.SUBSCRIPTION_PLAN);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-[#2A2A2A] rounded-2xl w-full max-w-md shadow-xl">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                    <h2 className="text-2xl font-bold text-black dark:text-white">
                        Complete Purchase
                    </h2>
                    {isInApp && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            <span className="ml-2 text-yellow-600 dark:text-yellow-400">
                                (Includes {appPlatformFeeRate * 100}% App Store fee)
                            </span>
                        </p>
                    )}
                </div>

                <div className="px-6 py-4 space-y-4">
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                        {product.coverImageUrl && (
                            <img
                                src={product.coverImageUrl}
                                alt={product.title}
                                className="w-20 h-20 rounded-lg object-cover"
                            />
                        )}
                        <div className="flex-1">
                            <h3 className="font-semibold text-lg text-black dark:text-white line-clamp-2">
                                {product.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {product.category}
                            </p>
                            {displayCurrency && product.currency &&
                                displayCurrency !== product.currency.toUpperCase() && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Price shown in {displayCurrency} for {userCountry || "your region"}
                                    </p>
                                )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between text-gray-700 dark:text-gray-300">
                            <span>Base Price</span>
                            <span className="font-semibold">
                                {currencySymbol}{originalBasePrice.toFixed(2)}
                            </span>
                        </div>

                        {isInApp && (
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>App Store fee (15%)</span>
                                <span className="font-semibold">
                                    +{currencySymbol}{(originalBasePrice * appPlatformFeeRate).toFixed(2)}
                                </span>
                            </div>
                        )}

                        {isFreeUser && (
                            <>
                                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                                    <span>Service Fee (5%)</span>
                                    <span className="font-semibold">
                                        +{currencySymbol}{serviceFee.toFixed(2)}
                                    </span>
                                </div>
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mt-2">
                                    <p className="text-xs text-yellow-800 dark:text-yellow-300">
                                        💡 <strong>Pro members pay no service fees!</strong> Upgrade now to save on this and all future purchases.
                                    </p>
                                </div>
                            </>
                        )}

                        <div className="flex justify-between text-xl font-bold text-black dark:text-white pt-2 border-t border-gray-300 dark:border-gray-600">
                            <span>Total</span>
                            <span>{currencySymbol}{finalTotal.toFixed(2)}</span>
                        </div>
                    </div>

                    {isFreeUser && (
                        <div className="bg-gradient-to-r from-[#327691] to-[#22516a] rounded-lg p-4 text-white">
                            <h4 className="font-semibold mb-1">Save with Pro Membership</h4>
                            <p className="text-sm mb-3 opacity-90">
                                Pay no service fees on any purchase. Plus unlock exclusive features!
                            </p>
                            <button
                                onClick={handleUpgrade}
                                className="w-full bg-white text-[#327691] font-semibold py-2 rounded-lg hover:bg-gray-100 transition"
                            >
                                Upgrade to Pro Member
                            </button>
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-4 py-3 text-xs md:text-[15px] rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handlePurchase}
                        disabled={loading}
                        className="flex-1 px-4 py-3 text-xs md:text-[15px] rounded-xl font-semibold bg-[#327691] text-white hover:bg-[#22516a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? "Processing..." :
                            `Pay ${currencySymbol}${finalTotal.toFixed(2)}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCheckout;